import { useContext, useEffect, useState } from 'react';
import orderBy from 'lodash/orderBy';

import { TableContext } from 'src/shared/components/data-table/DataTable';

import { SortingDirection, type TableRowsSortingFunction } from '../dataTableTypes';

const useDataTable = () => {
    const dataTableContext = useContext(TableContext);

    if (!dataTableContext) {
        throw new Error('useDataTable must be used with DataTableContext Provider');
    }

    const {
        rows: allRows,
        currentPageNumber,
        rowsPerPage,
        columns,
        sortedColumn,
        dispatch,
        searchText,
        onPageChange,
        onSort,
        isServerSideSort,
    } = dataTableContext;

    const [filteredRows, setFilteredRows] = useState(allRows);
    useEffect(() => {
        const filteredRows = !searchText
            ? allRows
            : [...allRows].filter(row => {
                  return row.cells.some((cell, index) => {
                      const { isSearchable = true } = columns[index];
                      if (!isSearchable) {
                          return false;
                      }
                      return cell?.toString().includes(searchText) || cell?.toString().toLowerCase().includes(searchText);
                  });
              });
        setFilteredRows(filteredRows);
    }, [searchText, allRows]);

    const getSortedCurrentPageRows = () => {
        const rows = [...filteredRows];
        const totalRows = filteredRows.length;
        const rowIndexLowerBound = (currentPageNumber - 1) * rowsPerPage;
        const rowIndexUpperBound = rowIndexLowerBound + rowsPerPage;

        const takePageSlice = (source: typeof rows) =>
            totalRows > rowsPerPage
                ? source.filter((_, rowIndex) => !(rowIndex < rowIndexLowerBound || rowIndexUpperBound - 1 < rowIndex))
                : source;

        if (isServerSideSort) {
            return takePageSlice(rows);
        }

        let sortedRows = rows;
        if (sortedColumn && sortedColumn.sortedDirection !== SortingDirection.NONE) {
            const sortedColumnIndex = columns.findIndex(column => column.columnId === sortedColumn.columnId);

            if (sortedColumnIndex > -1) {
                const sortFunction = columns[sortedColumnIndex].sortFn ?? plainSorter;
                sortedRows = sortFunction(sortedRows, sortedColumnIndex, sortedColumn.sortedDirection);
            }
        }

        return takePageSlice(sortedRows);
    };

    const setPageNumber = (value: number) => {
        dispatch({
            type: 'set_current_page_number',
            payload: value,
        });

        onPageChange?.(value);
    };

    return {
        getSortedCurrentPageRows,
        setPageNumber,
        filteredRows,
        onSort,
        ...dataTableContext,
    };
};

// useful for sorting strings and numbers in ascending or descending order
const plainSorter: TableRowsSortingFunction = (tableRows, columnIndex, direction) => {
    const iteratee = (data: any) => data.cells[columnIndex] as string | number;
    const orderDirection = direction === SortingDirection.DESC ? 'desc' : 'asc';
    return orderBy(tableRows, [iteratee], [orderDirection]);
};

export default useDataTable;
